import api from './api';
import { Product } from '../types';
import { getAuth } from 'firebase/auth';

/**
 * Wishlist Service
 * Handles wishlist-related API calls with backend integration
 * Falls back to localStorage when user is not authenticated
 */

const WISHLIST_STORAGE_KEY = 'topped_wishlist';

/**
 * Check if user is authenticated
 */
const isAuthenticated = (): boolean => {
  const auth = getAuth();
  return !!auth.currentUser;
};

export const wishlistService = {
  /**
   * Get all wishlist items
   * Uses backend API if authenticated, otherwise localStorage
   */
  getAll: async (): Promise<Product[]> => {
    try {
      if (isAuthenticated()) {
        const response = await api.get('/wishlist');
        return response.data.data || [];
      } else {
        // Fallback to localStorage for non-authenticated users
        const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
        if (savedWishlist) {
          return JSON.parse(savedWishlist);
        }
        return [];
      }
    } catch (error) {
      console.error('Error getting wishlist:', error);
      // Fallback to localStorage on error
      const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return savedWishlist ? JSON.parse(savedWishlist) : [];
    }
  },

  /**
   * Add product to wishlist
   */
  add: async (product: Product): Promise<void> => {
    try {
      if (isAuthenticated()) {
        await api.post('/wishlist', { product_id: product.id });
      } else {
        // Fallback to localStorage for non-authenticated users
        const wishlist = await wishlistService.getAll();
        const exists = wishlist.some(item => item.id === product.id);
        if (!exists) {
          wishlist.push(product);
          localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
        }
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  },

  /**
   * Remove product from wishlist
   */
  remove: async (productId: number): Promise<void> => {
    try {
      if (isAuthenticated()) {
        await api.delete(`/wishlist/${productId}`);
      } else {
        // Fallback to localStorage for non-authenticated users
        const wishlist = await wishlistService.getAll();
        const filtered = wishlist.filter(item => item.id !== productId);
        localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(filtered));
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  },

  /**
   * Check if product is in wishlist
   */
  isInWishlist: async (productId: number): Promise<boolean> => {
    try {
      if (isAuthenticated()) {
        const response = await api.get(`/wishlist/check/${productId}`);
        return response.data.data.in_wishlist;
      } else {
        // Fallback to localStorage for non-authenticated users
        const wishlist = await wishlistService.getAll();
        return wishlist.some(item => item.id === productId);
      }
    } catch (error) {
      console.error('Error checking wishlist:', error);
      return false;
    }
  },

  /**
   * Clear entire wishlist
   */
  clear: async (): Promise<void> => {
    try {
      if (isAuthenticated()) {
        await api.delete('/wishlist');
      } else {
        // Fallback to localStorage for non-authenticated users
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      throw error;
    }
  },

  /**
   * Get wishlist count
   */
  getCount: async (): Promise<number> => {
    try {
      if (isAuthenticated()) {
        const response = await api.get('/wishlist/count');
        return response.data.data.count;
      } else {
        // Fallback to localStorage for non-authenticated users
        const wishlist = await wishlistService.getAll();
        return wishlist.length;
      }
    } catch (error) {
      console.error('Error getting wishlist count:', error);
      return 0;
    }
  },

  /**
   * Sync localStorage wishlist with backend (called after login)
   */
  syncWithBackend: async (): Promise<void> => {
    try {
      if (!isAuthenticated()) {
        return;
      }

      // Get local wishlist
      const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!savedWishlist) {
        return;
      }

      const localWishlist: Product[] = JSON.parse(savedWishlist);

      // Add each local item to backend
      for (const product of localWishlist) {
        try {
          await api.post('/wishlist', { product_id: product.id });
        } catch (error) {
          console.error(`Error syncing product ${product.id}:`, error);
        }
      }

      // Clear local storage after successful sync
      localStorage.removeItem(WISHLIST_STORAGE_KEY);
    } catch (error) {
      console.error('Error syncing wishlist with backend:', error);
    }
  },
};

export default wishlistService;
